"use client";

import { motion } from "framer-motion";
import { Users, KanbanSquare, Bot, Clock, ArrowRight } from "lucide-react";
import { fadeUp, Section } from "./utils";

const steps = [
  {
    num: "01",
    title: "Pick your crew.",
    copy: "Choose AI agents with real specialties — sales, ops, client success. Each one has a personality, a toolkit, and a memory.",
    icon: Users,
    time: "5 minutes",
  },
  {
    num: "02",
    title: "Connect your workflow.",
    copy: "Map your pipeline stages, connect your channels and tools, upload your listings, fleet data, or service catalog. We handle the wiring.",
    icon: KanbanSquare,
    time: "15 minutes",
  },
  {
    num: "03",
    title: "Watch them work.",
    copy: "Your agents pick up leads, manage requests, coordinate with clients, and flag you only when they need a human decision.",
    icon: Bot,
    time: "Ongoing",
  },
];

export function HowItWorks() {
  return (
    <Section id="how-it-works" className="px-6 py-28 max-w-7xl mx-auto">
      <motion.div variants={fadeUp} className="text-center mb-14">
        <p className="text-sm font-medium text-coral-400 mb-3 uppercase tracking-wider">
          How it works
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-content">
          Up and running in an afternoon.
        </h2>
        <p className="mt-3 text-content-muted max-w-md mx-auto">
          No developers needed. No six-week integration. Connect your tools and
          let your AI crew take over.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={i}
              variants={fadeUp}
              custom={i}
              className="relative group"
            >
              <div className="bg-surface/60 border border-surface-border rounded-2xl p-8 hover:border-surface-hover transition-all duration-300 h-full">
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-4xl font-black text-surface-border group-hover:text-coral-500/20 transition-colors duration-500 select-none">
                    {s.num}
                  </span>
                  <div className="w-10 h-10 rounded-lg bg-surface-tertiary flex items-center justify-center">
                    <Icon size={18} className="text-content-secondary" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-content">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm text-content-muted leading-relaxed">
                  {s.copy}
                </p>
                <p className="mt-4 text-xs text-content-muted flex items-center gap-1.5">
                  <Clock size={12} />
                  {s.time}
                </p>
              </div>
              {i < 2 && (
                <div
                  className="hidden md:block absolute top-1/2 -right-3 w-6 border-t border-dashed border-surface-border"
                  aria-hidden="true"
                />
              )}
            </motion.div>
          );
        })}
      </div>

      <motion.div variants={fadeUp} custom={3} className="text-center mt-12">
        <button className="group bg-coral-500 hover:bg-coral-400 text-white font-semibold rounded-full px-8 py-3.5 text-sm transition-all duration-300 active:scale-95 shadow-lg shadow-coral-500/25 hover:shadow-xl hover:shadow-coral-500/40 cursor-pointer">
          Start Free Trial
          <ArrowRight
            size={16}
            className="inline ml-2 group-hover:translate-x-0.5 transition-transform"
          />
        </button>
        <p className="mt-3 text-xs text-content-muted">
          No credit card. No developers. Cancel anytime.
        </p>
      </motion.div>
    </Section>
  );
}
